import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import NightlightRoundIcon from "@mui/icons-material/NightlightRound";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { useNavigate } from "react-router-dom";
import RmfDialog, { DialogActions, DialogChip, DialogIcon, DialogText, DialogTitle, dialogButtonSx } from "../../Dialog/RmfDialog";
import { SelectField } from "../../Checkout/checkoutUi";
import { SLOT_STEP_MINUTES, generateDays, generateTimeSlots, isOpenNow } from "../../Checkout/orderTimeSlots";
import { useTimeSelect } from "../../../context/Time";
import { ORDER_MODES, useOrderMode } from "../../../context/OrderMode";
import UserContext from "../../../context/User";
import { nextOpeningLabel } from "../../../utils/helper";
import { restaurantDayOf, restaurantToday, timeIn } from "../../../utils/restaurantClock";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../../utils/designTokens";

// "Restaurant closed" popup, on the menu page and at checkout.
//
// It offers the three earliest times the restaurant can take an order (the
// same slots checkout's scheduler offers: restaurant hours, plus the prep time
// for pickup or 30 minutes for delivery), and "Schedule for …" sets that as the
// order time in the header, which checkout follows. "More times" opens the
// whole schedule inside the popup.
//
// `restaurant` is the restaurant object ({ _id, name, openingTimes,
// preparationTime, isAvailable, activeClosure }); a plain name still works.
// `onScheduled` runs after a time is chosen (checkout scrolls to its order
// options); `onBrowse` replaces "Browse the menu" closing the popup
// (checkout goes back to the menu).

// Everything here is on the restaurant's clock (`tz`, utils/restaurantClock.js):
// a slot shows its time there, and "Today" is the restaurant's today.
const timeLabel = (d, tz) => timeIn(d, tz);

// `day` is a restaurant calendar day (a local-midnight Date, see generateDays).
function dayLabel(day, tz) {
  const today = restaurantToday(tz);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (day.toDateString() === today.toDateString()) return "Today";
  if (day.toDateString() === tomorrow.toDateString()) return "Tomorrow";
  return day.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

// The day label of a slot (an instant).
const slotDayLabel = (slot, tz) => dayLabel(restaurantDayOf(slot, tz), tz);

// For the button: "11:15 AM", "tomorrow, 11:15 AM", "Fri, Oct 31, 11:15 AM".
function whenLabel(d, tz) {
  const day = slotDayLabel(d, tz);
  if (day === "Today") return timeLabel(d, tz);
  return `${day === "Tomorrow" ? "tomorrow" : day}, ${timeLabel(d, tz)}`;
}

function SlotCard({ slot, selected, onSelect, tz }) {
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        flex: "1 1 0",
        minWidth: 0,
        boxSizing: "border-box",
        minHeight: 64,
        px: 1,
        py: 1.25,
        borderRadius: RADIUS.control,
        textAlign: "center",
        bgcolor: selected ? C.redTint : C.bg,
        boxShadow: selected ? `inset 0 0 0 2px ${C.red}` : `inset 0 0 0 1px ${C.borderStrong}`,
        transition: "box-shadow .15s ease, background-color .15s ease",
        "&:hover": { boxShadow: selected ? `inset 0 0 0 2px ${C.red}` : `inset 0 0 0 1px ${C.ink}` },
      }}
    >
      <Box component="span" sx={{ display: "block", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 500, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {slotDayLabel(slot, tz)}
      </Box>
      <Box component="span" sx={{ display: "block", mt: 0.25, fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: C.ink, whiteSpace: "nowrap" }}>
        {timeLabel(slot, tz)}
      </Box>
    </Box>
  );
}

function RestaurantClose({ isVisible, toggleModal, restaurant, onScheduled, onBrowse }) {
  const info = typeof restaurant === "string" ? { name: restaurant } : restaurant || {};
  const tz = info.timezone || null;
  const navigate = useNavigate();
  const { selectScheduled } = useTimeSelect();
  const { orderMode } = useOrderMode() || {};
  const { restaurant: cartRestaurantId, cartCount } = useContext(UserContext) || {};
  const hasCart = Boolean(cartCount) && (!info._id || String(cartRestaurantId) === String(info._id));

  const isPickup = orderMode === ORDER_MODES.PICKUP;
  const leadMinutes = isPickup ? info.preparationTime ?? 15 : 30;
  // Switched off by the restaurant: nothing can be ordered, whatever the time.
  const takingOrders = info.isAvailable !== false;

  // Every slot the restaurant can take this week, by day. A holiday closure
  // (activeClosure) shuts today.
  const slotsByDay = useMemo(() => {
    if (!takingOrders || !isVisible) return [];
    return generateDays(7, tz)
      .map((day, i) => ({
        day,
        slots: i === 0 && info.activeClosure ? [] : generateTimeSlots(day, leadMinutes, info.openingTimes, SLOT_STEP_MINUTES, null, tz),
      }))
      .filter((d) => d.slots.length > 0);
  }, [takingOrders, isVisible, info.openingTimes, info.activeClosure, leadMinutes, tz]);
  const earliest = useMemo(() => slotsByDay.flatMap((d) => d.slots).slice(0, 3), [slotsByDay]);
  const canSchedule = earliest.length > 0;

  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (!isVisible) return;
    setSelected(earliest[0] || null);
    setShowAll(false);
  }, [isVisible, earliest]);

  const opens = takingOrders && !isOpenNow(info.openingTimes, tz) ? nextOpeningLabel(info.openingTimes, tz) : null;
  const name = info.name || "This restaurant";
  const close = () => toggleModal?.();

  const schedule = () => {
    if (!selected) return;
    selectScheduled(selected);
    close();
    onScheduled?.(selected);
  };

  // Full schedule: a day and a time.
  const selectedDayKey = selected ? restaurantDayOf(selected, tz).toDateString() : "";
  const daySlots = slotsByDay.find((d) => d.day.toDateString() === selectedDayKey)?.slots || [];

  const body = canSchedule
    ? hasCart
      ? "Your cart is saved. Schedule it for when they open, or keep browsing the menu."
      : "Schedule an order for when they open, or keep browsing the menu."
    : hasCart
      ? "Your cart is saved. They aren't taking orders right now, but you can keep browsing the menu."
      : "They aren't taking orders right now, but you can keep browsing the menu.";

  return (
    <RmfDialog open={isVisible} onClose={close}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", pr: 6, mb: 2.5 }}>
        <DialogIcon tone="gold">
          <NightlightRoundIcon />
        </DialogIcon>
        <DialogChip>{opens ? `Closed · opens ${opens}` : "Closed"}</DialogChip>
      </Box>

      <DialogTitle besideClose={false}>{`${name} is closed right now`}</DialogTitle>
      <DialogText>{body}</DialogText>

      {canSchedule && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.25 }}>
            <Box component="span" id="rmf-closed-earliest" sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: C.ink }}>
              {showAll ? "All times" : "Earliest times"}
            </Box>
            <Box
              component="button"
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
              sx={{ ...BUTTON_RESET, ...FOCUS_RING, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.redLink, borderRadius: "6px", "&:hover": { textDecoration: "underline" } }}
            >
              {showAll ? "Fewer times" : "More times"}
            </Box>
          </Box>

          {showAll ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
              <SelectField
                id="rmf-closed-day"
                label="Day"
                value={selectedDayKey}
                onChange={(key) => {
                  const d = slotsByDay.find((x) => x.day.toDateString() === key);
                  if (d) setSelected(d.slots[0]);
                }}
              >
                {slotsByDay.map((d) => (
                  <option key={d.day.toDateString()} value={d.day.toDateString()}>
                    {dayLabel(d.day, tz)}
                  </option>
                ))}
              </SelectField>
              <SelectField
                id="rmf-closed-time"
                label="Time"
                value={selected ? String(selected.getTime()) : ""}
                onChange={(ms) => setSelected(new Date(Number(ms)))}
              >
                {daySlots.map((s) => (
                  <option key={s.getTime()} value={String(s.getTime())}>
                    {timeLabel(s, tz)}
                  </option>
                ))}
              </SelectField>
            </Box>
          ) : (
            <Box role="radiogroup" aria-labelledby="rmf-closed-earliest" sx={{ display: "flex", gap: 1.25 }}>
              {earliest.map((slot) => (
                <SlotCard
                  key={slot.getTime()}
                  slot={slot}
                  selected={Boolean(selected) && selected.getTime() === slot.getTime()}
                  onSelect={() => setSelected(slot)}
                  tz={tz}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <DialogActions>
        {canSchedule && (
          <Box
            component="button"
            type="button"
            onClick={schedule}
            disabled={!selected}
            aria-label={selected ? `Schedule for ${whenLabel(selected, tz)}` : undefined}
            sx={dialogButtonSx.primary}
          >
            <CalendarMonthRoundedIcon aria-hidden="true" />
            {/* The day is on the selected card; phones drop it so the label stays on one line. */}
            {selected ? (
              <span>
                Schedule for{" "}
                {slotDayLabel(selected, tz) !== "Today" && (
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {`${whenLabel(selected, tz).replace(/, [^,]*$/, "")}, `}
                  </Box>
                )}
                {timeLabel(selected, tz)}
              </span>
            ) : (
              "Schedule"
            )}
          </Box>
        )}
        <Box
          component="button"
          type="button"
          onClick={() => {
            close();
            onBrowse?.();
          }}
          sx={canSchedule ? dialogButtonSx.secondary : dialogButtonSx.primary}
        >
          Browse the menu
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => {
            close();
            navigate("/restaurant-list?open=now");
          }}
          sx={dialogButtonSx.link}
        >
          See restaurants open now ›
        </Box>
      </DialogActions>
    </RmfDialog>
  );
}

export default React.memo(RestaurantClose);
